import { Component, type OnInit, DestroyRef, inject, effect, input, untracked } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, of, type Observable, shareReplay, switchMap, tap, BehaviorSubject, first, concat, merge, ignoreElements, switchMapTo, map } from 'rxjs';
import { MatSidenavModule } from '@angular/material/sidenav';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { type NavigationLink } from '@protctc/common/core/models/navigation-link';
import { UserService } from '@protctc/common/core/services/user.service';
import { CompaniesService } from '@protctc/common/core/services/companies.service';
import { UserRole } from '@protctc/common/core/models/user/user-role';
import { BreakpointObserverService } from '@protctc/common/core/services/breakpoint-observer.service';
import { routePaths } from '@protctc/common/core/utils/route-paths';
import { DialogsService } from '@protctc/common/shared/dialogs/dialogs.service';
import { WelcomeDialogComponent } from '@protctc/common/shared/dialogs/welcome-dialog/welcome-dialog.component';
import { type CompanyLogo } from '@protctc/common/core/models/company-logo';
import { CompanyService } from '@protctc/common/core/services/company.service';
import { filterNull } from '@protctc/common/core/rxjs/filter-null';
import { LogoService } from '@protctc/common/core/services/logo.service';
import { Company } from '@protctc/common/core/models/company/company';
import { LoadingDirective } from '@protctc/common/shared/directives/loading.directive';
import { ImageUploaderPreviewComponent } from '@protctc/common/shared/components/image-uploader-preview/image-uploader-preview.component';
import { HasUserRoleDirective } from '@protctc/common/shared/directives/has-user-role.directive';

import { BreadcrumbsComponent } from '../breadcrumbs/breadcrumbs.component';
import { MainLogoComponent } from '../components/main-logo/main-logo.component';

type NavigationMenuSettings = {

	/** Title. */
	title: string;

	/** Is submenu opened. */
	isOpened: boolean;
};

/** Sidebar component. */
@Component({
	selector: 'protctw-sidebar',
	templateUrl: './sidebar.component.html',
	styleUrls: ['./sidebar.component.css'],
	imports: [
		MatSidenavModule,
		CommonModule,
		LoadingDirective,
		ImageUploaderPreviewComponent,
		RouterModule,
		MainLogoComponent,
		HasUserRoleDirective,
		MatMenuModule,
		MatIconModule,
		MatTooltipModule,
		MatToolbarModule,
		BreadcrumbsComponent,
		MatButtonModule,
	],
})
export class SidebarComponent implements OnInit {
	private readonly userService = inject(UserService);

	private readonly breakpointObserverService = inject(BreakpointObserverService);

	private readonly router = inject(Router);

	private readonly dialogsService = inject(DialogsService);

	private readonly companyService = inject(CompanyService);

	private readonly logoService = inject(LogoService);

	private readonly companiesService = inject(CompaniesService);

	private readonly destroyRef = inject(DestroyRef);

	/** Current user data. */
	protected readonly currentUser$ = this.userService.currentUser$;

	/** Check the matching state of layout breakpoint stream.  */
	protected readonly isCatchBreakpoint$ = this.createCatchBreakpointStream();

	/** Represent user role like human-readable string. */
	protected readonly toReadableUserRole = UserRole.toReadable;

	/** User roles. */
	protected readonly userRole = UserRole;

	/** Route paths. */
	protected readonly routePaths = routePaths;

	/** Array all titles and flag is submenu of this title is opened. */
	private subMenuOpenedSettings: readonly NavigationMenuSettings[] = [];

	/** Company logo. */
	protected readonly companyLogo$ = this.initCompanyLogoStream();

	/** Navigation links. */
	protected readonly links$ = new BehaviorSubject<readonly NavigationLink[]>([]);

	/** Whether to show the merchant not ready banner. */
	protected readonly showMerchantNotReadyBanner$ = this.initMerchantNotReadyBannerStream();

	/** Links. */
	public readonly links = input<readonly NavigationLink[] | null>(null);

	public constructor() {
		effect(() => {
			const data = this.links();
			untracked(() => {
				if (data) {
					this.links$.next(data);
				}
			});
		});
	}

	/** @inheritdoc */
	public ngOnInit(): void {
		this.subscribeToFirstLogin();
		this.subscribeToSubMenuChanges();

		const addSubMenuOpenedSettingsSideEffect$ = this.links$.pipe(
			tap(links => {
				links.forEach(link => {
					const routePath = link.route.join('/');
					this.subMenuOpenedSettings = [
						...this.subMenuOpenedSettings,
						{ title: link.text, isOpened: this.router.url.includes(routePath) },
					];
				});
			}),
		);

		merge(
			addSubMenuOpenedSettingsSideEffect$,
		).pipe(
			ignoreElements(),
			takeUntilDestroyed(this.destroyRef),
		)
			.subscribe();
	}

	/**
	 * Check if is submenu of title opened.
	 * @param title Title.
	 */
	protected isOpenedMenu(title: string): boolean {
		return this.findSubmenuOpenedSettingByTitle(title)?.isOpened ?? false;
	}

	/**
	 * Toggle open submenu of title flag.
	 * @param title Title.
	 * @param event Event for prevent link redirection.
	 */
	protected toggleOpenMenu(title: string, event?: Event): void {
		if (event) {
			event.stopPropagation();
			event.preventDefault();
		}

		this.subMenuOpenedSettings.forEach(item => {
			if (item.title === title) {
				item.isOpened = !item.isOpened;
			}
		});
	}

	/** Logout. */
	protected logout(): void {
		this.userService.logout().pipe(
			takeUntilDestroyed(this.destroyRef),
		)
			.subscribe(() => this.router.navigateByUrl('/auth'));
	}

	/** Create catch breakpoint stream for change layout. */
	private createCatchBreakpointStream(): Observable<boolean> {
		return this.breakpointObserverService.createMobileBreakpointObserver().pipe(
			shareReplay({ bufferSize: 1, refCount: true }),
		);
	}

	/** Init company logo stream. */
	private initCompanyLogoStream(): Observable<CompanyLogo | null> {
		return concat(
			this.currentUser$.pipe(
				first(),
				filter(user => user !== null),
				filterNull(),
				switchMap(user => user.companyId ? this.companyService.getCompanyLogo(user.companyId) : of(null)),
				tap(logo => logo ? this.logoService.setLogo(logo) : undefined),
			),
			this.logoService.logo$,
		);
	}

	/** Subscribe to fist user login for show let's get started dialog. */
	private subscribeToFirstLogin(): void {
		this.userService.isFirstLogin$.pipe(
			filter(isFirstLogin => isFirstLogin),
			tap(() => this.dialogsService.openDialog(WelcomeDialogComponent)),
			takeUntilDestroyed(this.destroyRef),
		)
			.subscribe();
	}

	/** Subscribe to submenu changes. */
	private subscribeToSubMenuChanges(): void {
		this.router.events.pipe(
			filter(routerEvent => routerEvent instanceof NavigationEnd),
			switchMapTo(this.links$),
			takeUntilDestroyed(this.destroyRef),
		)
			.subscribe(links => {
				links.forEach(link => {
					const routePath = link.route.join('/');
					const isCurrentSubmenuSettingOpened = this.findSubmenuOpenedSettingByTitle(link.text)?.isOpened === true;
					const isUrlIncludeRoutePath = this.router.url.includes(routePath);
					const isShouldToggle = (link.children != null && (isUrlIncludeRoutePath && !isCurrentSubmenuSettingOpened)) ||
						(!isUrlIncludeRoutePath && isCurrentSubmenuSettingOpened);

					if (isShouldToggle) {
						this.toggleOpenMenu(link.text);
					}
				});
			});
	}

	/**
	 * Find submenu opened settings by title.
	 * @param title Title.
	 */
	private findSubmenuOpenedSettingByTitle(title: string): NavigationMenuSettings | undefined {
		return this.subMenuOpenedSettings.find(item => item.title === title);
	}

	/** Init merchant not ready banner stream. */
	private initMerchantNotReadyBannerStream(): Observable<boolean> {
		return this.userService.currentUser$.pipe(
			filterNull(),
			switchMap(user => {
				if (user.role === UserRole.BusinessOwner) {
					return this.companiesService.getCurrentCompany().pipe(
						map(company => !Company.isVerified(company)),
					);
				}
				return of(false);
			}),
			shareReplay({ refCount: true, bufferSize: 1 }),
		);
	}
}
